// Reception detail panel — RON-5469. Prototype region: `#detail-panel`.
//
// Same row shape as the list (§4) — there is no second fetch, and no "detail" endpoint. The panel
// renders the row the list already has.
//
// Two regions here carry the contract's sharpest rules: the compliance block has THREE states per
// line rather than two, and the action buttons are driven by the server's flags rather than by
// the row's status.

import { useTranslation } from "react-i18next";

import {
  COMPLIANCE_STATE,
  ROW_STATUS,
  getRowAvatarTone,
  getComplianceState,
  getInitials,
  getRowTone,
  getStatusLabelKey,
  pluralText,
} from "../receptionConstants.js";

const Row = ({ label, children }) => (
  <>
    <dt className="reception-kv__k">{label}</dt>
    <dd className="reception-kv__v">{children}</dd>
  </>
);

// THE THREE-STATE LINE.
//
// §4: "`hs_acknowledged` and `nda_signed` are ABSENT, not `false`. `false` would say 'we asked and
// they refused', which is a different fact about a person standing at a desk, and a red cross on
// your panel would state it as truth."
//
// So "not recorded" is rendered as its own neutral state — never a cross, never a tick, never a
// blank. A blank row would read as "nothing to report", which is the same lie in quieter clothes.
const ComplianceLine = ({ label, state }) => {
  const { t } = useTranslation("common");

  if (state === COMPLIANCE_STATE.NOT_RECORDED) {
    return (
      <Row label={label}>
        <span className="reception-kv__muted">{t("react_reception_not_recorded")}</span>
      </Row>
    );
  }

  const ok = state === COMPLIANCE_STATE.YES;

  return (
    <Row label={label}>
      <span className={ok ? "reception-kv__ok" : "reception-kv__bad"}>
        {ok ? "✓ " : "✗ "}
        {t(ok ? "react_reception_yes" : "react_reception_no")}
      </span>
    </Row>
  );
};

// The prototype puts an icon on every action button; ours had none, which left four bare words in
// a 2×2 grid. These are its own paths, so the same action carries the same glyph here and in the
// row menu — a receptionist learns the shape once.
const ActionIcon = ({ children }) => (
  <svg
    className="reception-btn__icon"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {children}
  </svg>
);

const ACTION_ICONS = {
  checkIn: (
    <>
      <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
      <polyline points="10 17 15 12 10 7" />
      <line x1="15" y1="12" x2="3" y2="12" />
    </>
  ),
  checkOut: (
    <>
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </>
  ),
  notify: (
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
  ),
  vip: (
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
  ),
  badge: (
    <>
      <polyline points="6 9 6 2 18 2 18 9" />
      <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
      <rect x="6" y="14" width="12" height="8" />
    </>
  ),
  edit: (
    <>
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
    </>
  ),
  override: (
    <>
      <circle cx="12" cy="12" r="10" />
      <path d="M12 16v-4M12 8h.01" />
    </>
  ),
};

const ReceptionDetailPanel = ({
  row,
  isBusy,
  onClose,
  onCheckIn,
  onCheckOut,
  onToggleVip,
  onNotifyHost,
  // Called by the three buttons that have no endpoint, with the locale key explaining which one.
  // The panel does not decide HOW that is shown — the shell owns the toaster.
  onUnavailable,
}) => {
  const { t } = useTranslation("common");

  if (!row) {
    return (
      <aside className="reception-detail reception-detail--empty">
        <p className="reception-detail__placeholder">{t("react_reception_select_a_visitor")}</p>
      </aside>
    );
  }

  const tone = getRowTone(row);

  return (
    <aside className="reception-detail" aria-label={t("react_reception_detail")}>
      <button
        type="button"
        className="reception-detail__close"
        onClick={onClose}
        aria-label={t("react_reception_close_details")}
      >
        {/* An SVG, not the `✕` glyph we had. A text character is sized by the font and sits on its
            baseline, so it never centres in a 28px box the way a stroked icon does — and the
            prototype's `svg { width: 14px }` rule had nothing to act on. */}
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>

      <div className="reception-detail__head">
        {/* The prototype's `.detail-eyebrow`, which we had omitted entirely. It is not decoration:
            this panel and the list show the same person, and the eyebrow is what says the panel is
            the LIVE record rather than a frozen copy of the row that was clicked. The green dot is
            the same promise the top bar's "live" pill makes. */}
        <p className="reception-detail__eyebrow">
          <span className="reception-detail__pulse" aria-hidden="true" />
          {t("react_reception_selected_visitor")}
        </p>

        {/* The prototype's `.detail-person`: the head is a COLUMN (eyebrow, then person, then
            status), and the avatar/name pair is the row inside it. Ours made the head itself the
            row, which left nowhere for the eyebrow to sit. */}
        <div className="reception-detail__person">
        <span
          className={`reception-avatar reception-avatar--${getRowAvatarTone(row)} reception-avatar--lg${
            row.isVip ? " reception-avatar--vip" : ""
          }`}
        >
          {getInitials(row.name)}
        </span>
        <div>
          <h2 className="reception-detail__name">
            {row.name}
            {/* Beside the NAME, not in the status row below. The prototype builds it into the
                name itself, and it belongs there: being a VIP is a fact about the person,
                where the pill underneath is a fact about this visit. The list already pairs
                them the same way. */}
            {row.isVip && <span className="reception-tag reception-tag--vip">VIP</span>}
          </h2>
          <p className="reception-detail__sub">
            {[
              row.company,
              t(
                row.kind === "contractor"
                  ? "react_reception_kind_contractor"
                  : "react_reception_kind_visitor"
              ),
            ]
              .filter(Boolean)
              .join(" · ")}
          </p>
        </div>
        </div>

        <div className="reception-detail__status">
        <span className={`reception-pill reception-pill--${tone}`}>
          {tone === "overstay" && row.overstayBy != null
            ? pluralText(t, "react_reception_overstay_by", row.overstayBy)
            : t(getStatusLabelKey(row.status))}
        </span>
        {row.isWalkIn && <span className="reception-tag">{t("react_reception_walk_in")}</span>}
        </div>
      </div>

      {/* The prototype's `.detail-body`: the head stays put and THIS scrolls. Ours made the
          whole panel the scroller, so the name and status scrolled away with the cards. */}
      <div className="reception-detail__body">

      <section className="reception-detail__section">
        <h3>{t("react_reception_visit_details")}</h3>
        <dl className="reception-kv">
          <Row label={t("react_reception_col_host")}>{row.host || "—"}</Row>
          <Row label={t("date")}>{row.startDate}</Row>
          <Row label={t("react_reception_arrival")}>{row.arrivalTime || "—"}</Row>
          <Row label={t("react_reception_expected_out")}>{row.expectedOut || "—"}</Row>
          {row.checkedInAt && (
            <Row label={t("react_reception_checked_in_at")}>{row.checkedInAt}</Row>
          )}
          {row.checkedOutAt && (
            <Row label={t("react_reception_checked_out_at")}>{row.checkedOutAt}</Row>
          )}
          <Row label={t("react_reception_booking_code")}>{row.bookingCode || "—"}</Row>
          {/* One contractor visit can cover a crew of six, and the muster counts people rather
              than rows — so a party of more than one says so. */}
          {row.personnel > 1 && (
            <Row label={t("react_reception_party")}>
              {pluralText(t, "react_reception_personnel", row.personnel)}
            </Row>
          )}
          {/* `purpose` carries `work_description` for a contractor, so one field serves both. */}
          <Row label={t("react_reception_purpose")}>{row.purpose || "—"}</Row>
        </dl>
      </section>

      {row.contractor && (
        <section className="reception-detail__section">
          <h3>{t("react_reception_vetting")}</h3>
          <dl className="reception-kv">
            <Row label={t("react_reception_trade")}>{row.contractor.trade || "—"}</Row>
            <Row label={t("status")}>{row.contractor.status}</Row>
            <Row label={t("react_reception_vetted_until")}>
              {row.contractor.vettedUntil || "—"}
            </Row>
          </dl>
          {/* Said before the receptionist presses Check in, not after the server refuses: the
              person is standing at the desk, and being told why in advance is the difference
              between explaining and apologising. */}
          {row.contractor.isProvisional && (
            <p className="reception-detail__warn">{t("react_reception_provisional_warning")}</p>
          )}
        </section>
      )}

      <section className="reception-detail__section">
        <h3>{t("react_reception_compliance")}</h3>
        <dl className="reception-kv">
          <ComplianceLine
            label={t("react_reception_hs_form")}
            state={getComplianceState(row.compliance, "hsAcknowledged")}
          />
          <ComplianceLine
            label={t("react_reception_nda")}
            state={getComplianceState(row.compliance, "ndaSigned")}
          />
          <ComplianceLine
            label={t("react_reception_photo")}
            state={getComplianceState(row.compliance, "photoCaptured")}
          />
          <Row label={t("react_reception_badge")}>
            {row.compliance?.badgeNumber
              ? `${row.compliance.badgeNumber} · ${row.compliance.badgeState}`
              : t("react_reception_no_badge")}
          </Row>
        </dl>
      </section>

      <section className="reception-detail__section">
        <h3>{t("react_reception_actions")}</h3>
        {/* FIVE BUTTONS, in the prototype's order and layout: four in a 2×2 grid and the override
            spanning both columns underneath.

            Three of them have no endpoint behind them. They are RENDERED ANYWAY and say so when
            pressed, because a receptionist at a desk needs to tell "this screen cannot do that"
            apart from "I cannot find the button" — and silence reads as the second. What they must
            never do is look like they worked. See docs/RON-5469_open-questions.md. */}
        <div className="reception-detail__actions">
          <button
            type="button"
            className="reception-btn"
            disabled={isBusy}
            onClick={() => onUnavailable?.("react_reception_unavailable_badge")}
          >
            <ActionIcon>{ACTION_ICONS.badge}</ActionIcon>
            {t("react_reception_reprint_badge")}
          </button>

          <button
            type="button"
            className="reception-btn reception-btn--ghost"
            disabled={isBusy}
            onClick={() => onNotifyHost?.(row)}
          >
            <ActionIcon>{ACTION_ICONS.notify}</ActionIcon>
            {t("react_reception_notify_host")}
          </button>

          <button
            type="button"
            className="reception-btn reception-btn--ghost"
            disabled={isBusy}
            onClick={() => onUnavailable?.("react_reception_unavailable_edit")}
          >
            <ActionIcon>{ACTION_ICONS.edit}</ActionIcon>
            {t("react_reception_edit_details")}
          </button>

          {/* The prototype's fourth slot is "Sign out", because its fixture shows somebody already
              in the building. Ours switches on the SERVER's flags (§4), so the same slot offers
              check-in for an expected visitor — the action that slot is for, either way. */}
          {row.canCheckIn ? (
            <button
              type="button"
              className="reception-btn"
              disabled={isBusy}
              onClick={() => onCheckIn?.(row)}
            >
              <ActionIcon>{ACTION_ICONS.checkIn}</ActionIcon>
              {t("react_reception_check_in")}
            </button>
          ) : (
            <button
              type="button"
              className="reception-btn reception-btn--danger"
              disabled={!row.canCheckOut || isBusy}
              onClick={() => onCheckOut?.(row)}
            >
              <ActionIcon>{ACTION_ICONS.checkOut}</ActionIcon>
              {t("react_reception_check_out")}
            </button>
          )}

          <button
            type="button"
            className="reception-btn reception-btn--ghost reception-btn--full"
            disabled={isBusy}
            onClick={() => onUnavailable?.("react_reception_unavailable_override")}
          >
            <ActionIcon>{ACTION_ICONS.override}</ActionIcon>
            {t("react_reception_manual_override")}
          </button>
        </div>

        {/* Not built, and said out loud rather than shown as a dead button. §9: badge printing is
            an integration item and `vcm.visitor.block` is a capability with no table behind it. */}
        {row.status === ROW_STATUS.CHECKED_IN && (
          <p className="reception-detail__note">{t("react_reception_badge_print_note")}</p>
        )}
      </section>
      </div>
    </aside>
  );
};

export default ReceptionDetailPanel;
